import { gql, useQuery } from '@apollo/client';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type {
  PostDraftBarQuery,
  PostDraftBarQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { PostType } from '../graphql/generated/graphqlTypes';
import publicIcon from '../icons/public.svg';
import { UserRole } from '../topic/topicPolicyCheck';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import PostDraftBarPublishButton from './PostDraftBarPublishButton';
import { getStateIcon } from './PostIcon';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from './postPolicyCheck';
import PostViewStateBar from './PostViewStateBar';

const POST_QUERY = gql`
  query PostDraftBar($id: ID!) {
    post(id: $id) {
      id
      type
      ...PostDraftBarPublishButton_post
      ...isAllowedThroughLinkAccess_post
    }
  }
  ${PostDraftBarPublishButton.fragments.post}
  ${isAllowedThroughLinkAccess.fragments.post}
`;

interface PostDraftBarProps {
  postId: string;
}

const PostDraftBar = ({ postId }: PostDraftBarProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const { data } = useQuery<PostDraftBarQuery, PostDraftBarQueryVariables>(
    POST_QUERY,
    { variables: { id: postId } },
  );
  const post = data?.post;

  if (!post) return null;

  const isPublic = isAllowedThroughLinkAccess(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Anonymous,
  );

  return (
    <PostViewStateBar
      actions={<PostDraftBarPublishButton post={post} size="small" />}
    >
      <SVGIcon>
        {getStateIcon({
          type: post.type,
          draft: true,
          archived: false,
        })}
      </SVGIcon>
      <strong>
        {post.type === PostType.Template ? 'Draft Template' : 'Draft'}
      </strong>
      {isPublic && (
        <>
          {!isMobile && (
            <div>Publish to make this discoverable by teammates</div>
          )}
          <Tooltip
            tooltip="Logged out visitors can access this post with its URL"
            placement="bottom"
            trigger="hover"
            oneLine
          >
            <SVGIcon>{publicIcon}</SVGIcon>
          </Tooltip>
        </>
      )}
    </PostViewStateBar>
  );
};

export default PostDraftBar;
